import {
  Component,
  OnInit,
  Input,
  HostListener,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef, SimpleChanges
} from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { BootstrapMembersTabsComponent } from "src/app/members/details-page/bootstrap-members-tabs/bootstrap-members-tabs.component";
import { Route, Router, NavigationStart, ActivationEnd } from "@angular/router";
import { first, filter, tap, map } from "rxjs/operators";
import { animate, style, transition, trigger, state, sequence } from '@angular/animations';
import { AfterViewInit, OnChanges } from '@angular/core';

export interface IThirdNavCollapsibleElement {
  name: string;
  route: string;
  external?: boolean;
  onClick?: () => Observable<any>;
  // Used to match nested level on navigation. For Example /entries/news when we are in a expand with navigation.
  matchRoute?: string;
  /* Used to match nested level on navigation allowed for the first level.
   For Example /entries/news when we are in a expand with navigation must mark as active the /entries on the first level,
   so we need to add ['entries/news'] for the ['entries'] route.
   */
  matchesRoutes?: Array<string>
  mustBeFullCheck?: boolean;
}

/**
 * Bar navigation used in events pages.
 *
 * It's names 'Third nav tab' as is the third menu
 * that appears in https://worldarchery.sport/events/indoor/team-ranking
 *
 * @export
 * @class ThirdNavTabComponentComponent
 * @implements {OnInit}
 */
@Component({
  selector: "app-third-nav-tab-component",
  templateUrl: "./third-nav-tab-component.component.html",
  styleUrls: ["./third-nav-tab-component.component.scss"],
  animations: [
    trigger('heightAnimation', [
      transition(':enter', [
        style({ height: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 0 }),
        animate('300ms', style({ height: '*', opacity: 1 }))
      ]),

      transition(':leave', [
        animate('300ms', style({ height: 0, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, opacity: 0 }))
      ])
    ]),

    trigger('opacityAnimation', [
      state('out', style({
        opacity: 0
      })),
      state('in', style({
        opacity: 1
      })),
      transition('out<=>in', animate('300ms'))
    ])
  ]
})
export class ThirdNavTabComponentComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input()
  elements: Array<IThirdNavCollapsibleElement>;
  @Input()
  selectedIndex: number;
  changedIndex: number;
  heightAnimationState = 'closed';
  slideAnimationState = 'out';


  /**
   * Number of elements to be displayed
   *
   * @type {number}
   * @memberof ThirdNavTabComponentComponent
   */
  @Input()
  elementsLimit: number = 4;

  /**
   * Number of elements to be displayed
   *
   * @type {number}
   * @memberof ThirdNavTabComponentComponent
   */
  @Input()
  maxElements: number = 4;

  /**
   * Number of elements to be displayed in tablet
   *
   * @type {number}
   * @memberof ThirdNavTabComponentComponent
   */
  @Input()
  maxElementsTablet: number = 3;


  /**
   * Container self reference for get the width of parent
   *
   * @type {ElementRef}
   * @memberof ThirdNavTabComponentComponent
   */
  @ViewChild('menu', { static: false })
  navMenu: ElementRef;

  /**
   * The color of the tab when active. Default whyte (#ffffff)
   *
   * @memberof ThirdNavTabComponentComponent
   */
  @Input()
  color_active = "#ffffff";
  visibility: boolean = false;
  isMobile: boolean = false;
  isTablet: boolean = false;
  isActive: boolean = false;

  /**
   * A property for set the subscription and destroy them on component destroy
   *
   * @private
   * @type {Array<Subscription>}
   * @memberof ThirdNavTabComponentComponent
   */
  private subscriptions: Array<Subscription> = [];

  /**
   * Number of elements ot hide.
   *
   * @type {number}
   * @memberof ThirdNavTabComponentComponent
   */
  numberOfElementsToHide: number = 0;

  /**
   * Creates an instance of ThirdNavTabComponentComponent.
   *
   * @memberof ThirdNavTabComponentComponent
   */
  constructor(
    breakpointObserver: BreakpointObserver,
    private router: Router,
    private element: ElementRef,
    private chg: ChangeDetectorRef
  ) {
    this.subscriptions.push(
      breakpointObserver
        .observe([
          '(max-width: 1276px)',
          Breakpoints.Tablet,
          Breakpoints.TabletPortrait,
          Breakpoints.TabletLandscape])
        .subscribe(result => {
          if (result.matches) {
            this.isTablet = true;
            if (breakpointObserver.isMatched([Breakpoints.XSmall])) {
              this.isTablet = false;
              this.isMobile = true;

            } else {
              this.isMobile = false;
              // this.isTablet=true;
            }
          } else {
            if (!breakpointObserver.isMatched([Breakpoints.XSmall])) {
              this.isMobile = false;
            }
            this.isTablet = false;
          }
        }),
    );
  }

  /**
   * Implements ngOnInit().
   *
   * @memberof ThirdNavTabComponentComponent
   */
  ngOnInit() {
    if (this.elements == null) {
      throw new Error(
        'Not input of elements has been provided. Please use it in the template <app-third-nav-tab-component [elements]="my-data">'
      );
    }
    // Set the first time because there's not ActivationEnd

    this.subscriptions.push(
      this.router.events
        .pipe(filter(a => a instanceof ActivationEnd))
        .subscribe(next => {
          this.setRouteToIndex();
        })
    );
    if (this.selectedIndex === undefined) {
      this.selectedIndex = 0;
    }
  }

  /**
   *
   * @inheritdoc
   * @memberof ThirdNavTabComponentComponent
   */
  ngAfterViewInit(): void {
    this.numberOfElementsToHide = this.elementsToHide;
    this.setRouteToIndex();
    this.chg.detectChanges();
  }

  /**
   *
   * @inheritdoc
   * @memberof ThirdNavTabComponentComponent
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(value => value.unsubscribe());
  }

  /**
   * {@inheritdoc}
   *
   * @param {SimpleChanges} changes
   * @memberof ThirdNavTabComponentComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    // We need to track the changes of elements array for set the route when the page bootstrap has changes.
    if ('elements' in changes) {
      this.setRouteToIndex();
    }

  }

  /**
   * A method for set the index to the correct value depending on navigation or in the component
   * init
   *
   * @memberof ThirdNavTabComponentComponent
   */
  setRouteToIndex() {
    this.elements.forEach((value, i) => {
      const splittedUrl = this.router.url.split('?')[0].split("/");
      const splittedRoute = value.route.split("/");
      // First we check if any route must be setted in the parent element.
      if (value.matchesRoutes) {
        const someRouteMatch = value.matchesRoutes.findIndex(route => splittedUrl.join('/').includes(route)) > -1;
        if (someRouteMatch) {
          this.selectedIndex = i;
          this.selectIndex(i);
        }
      }
      // After we check routes at second level.
      if (value.matchRoute) {
        if (splittedUrl.join('/').includes(value.matchRoute)) {
          this.selectedIndex = i;
          this.selectIndex(i);
        }
      } else {

        // Perform the old logic.
        if (
          value.mustBeFullCheck &&
          splittedRoute.join('/') === splittedUrl.join('/')
        ) {
          this.selectedIndex = i;
          this.selectIndex(i);
        } else {
          if (
            !value.mustBeFullCheck &&
            splittedUrl[splittedUrl.length - 1].includes(
              splittedRoute[splittedRoute.length - 1])
          ) {
            this.selectedIndex = i;
            this.selectIndex(i);
          }
        }
      }
    });
  }


  /**
   * Select a tab.
   *
   * @param {number} number
   *   Tab number.
   * @memberof ThirdNavTabComponentComponent
   */
  selectIndex(number: number) {
    const elementsLimit = this.isTablet ? this.maxElementsTablet : this.maxElements;
    if (number > elementsLimit - 1) {
      this.changedIndex = elementsLimit - 1;
    } else {
      this.changedIndex = undefined;
    }
    this.selectedIndex = number;
    this.visibility = false;
    this.heightAnimationState = 'closed';
    this.slideAnimationState = 'out';
  }


  /**
   * Method to hide or show the menu
   *
   * @memberof ThirdNavTabComponentComponent
   */
  toggleVisibility() {
    this.visibility = !this.visibility;
    this.isActive = !this.isActive;
    this.heightAnimationState = this.heightAnimationState === 'closed' ? 'open' : 'closed';
    this.slideAnimationState = this.slideAnimationState === 'out' ? 'in' : 'out';
  }

  /**
   * Elements to hide when window is resized
   *
   * @readonly
   * @memberof ThirdNavTabComponentComponent
   */
  get elementsToHide() {
    if (this.element.nativeElement.parentElement != null) {
      if (
        this.element.nativeElement.parentElement.offsetWidth >
        this.elements.length * (200 + 10)
      ) {
        return Math.min(Math.max(this.elements.length), this.maxElements, this.maxElements);
      } else {
        const elementsToHide =
          (((this.elements.length) * (100 + 200)) /
            this.element.nativeElement.parentElement.offsetWidth) %
          200;
        return this.maxElements - Math.round(elementsToHide);
      }
    }
  }

  /**
   * Listener to host on resize events to set the elements to hide.
   *
   * @param {*} event
   * @memberof ThirdNavTabComponentComponent
   */
  @HostListener('window:resize')
  onResize(event) {
    this.numberOfElementsToHide = this.elementsToHide;
  }

  getArrayCopy() {
    return Array.from(this.elements);
  }
}
